import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export function useAiAssistantSettings() {
  const { user } = useAuth();
  const [lastSessionId, setLastSessionId] = useState<string | null>(null);
  const [userMetadata, setUserMetadata] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [profileExists, setProfileExists] = useState<boolean>(false);
  const [aiMode, setAiMode] = useState<'expert' | 'conversation'>('conversation');

  // Ensure user profile exists
  const ensureUserProfile = async (): Promise<boolean> => {
    if (!user?.id) {
      return false;
    }

    try {
      // Check if profile already exists
      const { data, error } = await supabase
        .from('assistant_user_profiles')
        .select('user_id')
        .eq('user_id', user.id)
        .single();
      
      if (error && error.code === 'PGRST116') {
        // Profile doesn't exist, create it with defaults
        console.log('Creating new user profile with default settings');
        const { error: insertError } = await supabase
          .from('assistant_user_profiles')
          .insert({
            user_id: user.id,
            metadata: {
              auto_load_last_session: true,
              ai_mode: 'conversation' // Default to conversation mode
            },
            updated_at: new Date().toISOString()
          });
          
        if (insertError) {
          console.error('Error creating default user profile:', insertError);
          return false;
        }
        
        setUserMetadata({ auto_load_last_session: true, ai_mode: 'conversation' });
        setProfileExists(true);
        return true;
      } else if (error) {
        console.error('Error checking user profile:', error);
        return false;
      }
      
      // Profile exists
      setProfileExists(true);
      return true;
    } catch (err) {
      console.error('Unexpected error in ensureUserProfile:', err);
      return false;
    }
  };

  // Fetch settings from Supabase
  useEffect(() => {
    const fetchSettings = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        const { data, error } = await supabase
          .from('assistant_user_profiles')
          .select('last_session, metadata')
          .eq('user_id', user.id)
          .single();

        if (error) {
          if (error.code === 'PGRST116') {
            // Not found - will create with defaults when saving
            console.log('No settings found, will use defaults');
            // Automatically create profile with defaults
            await ensureUserProfile();
          } else if (error.code === 'PGRST205') {
            // Table doesn't exist error
            console.error('Table assistant_user_profiles does not exist:', error);
            toast.error('Database setup incomplete. Using default settings.');
          } else {
            console.error('Error fetching AI assistant settings:', error);
            toast.error('Failed to load AI assistant settings');
          }
        } else {
          setProfileExists(true);
          if (data?.last_session) {
            setLastSessionId(data.last_session);
          }
          if (data?.metadata) {
            setUserMetadata(data.metadata);
            // Set AI mode from metadata or default to conversation
            if (data.metadata.ai_mode) {
              setAiMode(data.metadata.ai_mode);
            }
          }
        }
      } catch (err) {
        console.error('Unexpected error fetching settings:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, [user?.id]);

  // Update last session
  const updateLastSession = async (sessionId: string | null) => {
    if (!user?.id) {
      return false;
    }
    
    try {
      // Ensure profile exists before updating
      if (!profileExists) {
        await ensureUserProfile();
      }
      
      // Handle empty string or null by setting last_session to null in the database
      const { error } = await supabase
        .from('assistant_user_profiles')
        .update({ 
          last_session: sessionId || null, // Convert empty strings to null for DB
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);
        
      if (error) {
        console.error('Error updating last session:', error);
        return false;
      }
      
      setLastSessionId(sessionId);
      return true;
    } catch (err) {
      console.error('Unexpected error updating last session:', err);
      return false;
    }
  };

  // Update AI mode preference
  const updateAiMode = async (mode: 'expert' | 'conversation'): Promise<boolean> => {
    if (!user?.id) {
      return false;
    }
    
    try {
      // Ensure profile exists before updating
      if (!profileExists) {
        await ensureUserProfile();
      }
      
      const updatedMetadata = { ...(userMetadata || {}), ai_mode: mode };
      
      const { error } = await supabase
        .from('assistant_user_profiles')
        .update({ 
          metadata: updatedMetadata,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);
        
      if (error) {
        console.error('Error updating AI mode:', error);
        return false;
      }
      
      setUserMetadata(updatedMetadata);
      setAiMode(mode);
      return true;
    } catch (err) {
      console.error('Unexpected error updating AI mode:', err);
      return false;
    }
  };

  // Update user metadata
  const updateUserMetadata = async (newMetadata: Record<string, any>) => {
    if (!user?.id) {
      return false;
    }
    
    try {
      // Ensure profile exists before updating
      if (!profileExists) {
        await ensureUserProfile();
      }
      
      const updatedMetadata = { ...(userMetadata || {}), ...newMetadata };
      
      const { error } = await supabase
        .from('assistant_user_profiles')
        .update({ 
          metadata: updatedMetadata,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);
        
      if (error) {
        console.error('Error updating user metadata:', error);
        return false;
      }
      
      setUserMetadata(updatedMetadata);
      
      // Update AI mode if it was changed
      if (newMetadata.ai_mode && newMetadata.ai_mode !== aiMode) {
        setAiMode(newMetadata.ai_mode);
      }
      
      return true;
    } catch (err) {
      console.error('Unexpected error updating user metadata:', err);
      return false;
    }
  };

  return { 
    isLoading,
    lastSessionId,
    userMetadata,
    aiMode,
    updateLastSession,
    updateUserMetadata,
    updateAiMode,
    ensureUserProfile,
    profileExists
  };
}
